import React, { useState, useEffect } from "react";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";
// import { useNavigate } from "react-router-dom";
import { ASSIGN_NUM_TO_CLIENT } from "../../../../utils/GraphQL/mutations";
import { GET_CLIENTS_FOR_PHONE } from "../../../../utils/GraphQL/queries";
import "./msgPageContactOptions.css";
import useMediaQuery from "@mui/material/useMediaQuery";
import ClientSearchInput from "../../adminComponents/ClientSearchInput/ClientSearchInput";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

// Phone-uniqueness (2026-09-04): the assign menu is owner-aware. It shows the
// number's CURRENT owner(s) (clients + staff, via getClientsForPhone), and
// when the number already has other owners the assign flow asks add-vs-move:
// ADD keeps existing owners (shared number), MOVE pulls them first. Snackbar
// comes from the global context (the old handleSnack prop was never passed by
// MessagesPage, so post-assign feedback silently threw).
const MsgPageContactOptions = ({
  anchorEl,
  handleClose,
  /*
   * The phone id of the row whose KEBAB is open — not the selected thread.
   *
   * This was `selectedId` and it was wrong for every item below: the kebab
   * calls stopPropagation, so the row's onClick never sets the selection, and
   * the menu therefore acted on whichever thread happened to be open. Create
   * New Client adopted the wrong number; "Assign To Client" / "Move To X Only"
   * rewrote ownership of the wrong number on an existing client.
   *
   * Renamed rather than re-pointed so any reference missed in the rewire fails
   * the CI=true build instead of silently reading the old value.
   */
  phnId,
  handleUpdate,
  handleNewClientDisplay,
}) => {
  //   const navigate = useNavigate();
  const matchesMQ = useMediaQuery("(min-width: 900px)");
  const { handleSnack } = useSnackbar();
  const [searchOpen, setSearchOpen] = useState(false);
  // The client picked from search while we ask add-vs-move (null = no ask).
  const [pendingClient, setPendingClient] = useState(null);
  const [assignNumCall] = useMutation(ASSIGN_NUM_TO_CLIENT);
  const [queryOwners, { data: ownersData }] = useLazyQuery(
    GET_CLIENTS_FOR_PHONE,
    { fetchPolicy: "network-only" }
  );

  const owners = ownersData?.getClientsForPhone || [];

  // Refresh the owner list whenever the menu opens on a selected thread.
  useEffect(() => {
    if (anchorEl && phnId) {
      queryOwners({ variables: { phnId } });
    }
    if (!anchorEl) {
      setPendingClient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl, phnId]);

  const ownerLabel = (o) => {
    const name = `${o?.firstName || ""} ${o?.lastName || ""}`.trim();
    return o?.ownerType === "user" ? `${name} (Staff)` : name;
  };

  const fireAssign = async (client, move) => {
    const match = client?.phoneNumberIds?.filter((id) => id === phnId);
    const name = `${client?.firstName} ${client?.lastName}`;

    const { data } = await assignNumCall({
      variables: {
        clientFullName: name?.trim(),
        clientId: client?._id,
        phoneNumId: phnId,
        localTime: Date.now().toString(),
        clientAlreadyHasId: match?.length > 0,
        move: move === true,
      },
    });

    if (data?.assignNumToClient) {
      handleSnack(
        move
          ? `Number moved to ${name}`
          : `Number assigned to ${name}`,
        2000
      );
      handleUpdate({ ...data?.assignNumToClient });
      setPendingClient(null);
      handleClose(true);
      return;
    }

    handleSnack("Something went wrong. Number not assigned.", 2500);
    setPendingClient(null);
  };

  const resultsToAssignNum = async (client) => {
    if (!phnId || phnId?.length <= 0 || !client) {
      handleSnack("No Number Selected. Not assigned to client.", 2000);
      return;
    }

    // Other owners = every current owner that isn't the picked client.
    const otherOwners = owners.filter(
      (o) => !(o?.ownerType === "client" && o?.ownerId === client?._id)
    );

    if (otherOwners.length > 0) {
      // Ask add-vs-move — the menu re-renders with the choice items.
      setPendingClient(client);
      setSearchOpen(false);
      return;
    }

    await fireAssign(client, false);
  };

  if (searchOpen) {
    return (
      <ClientSearchInput
        menuReturn={{
          isMenu: true,
          anchor: anchorEl,
          disableScrollLock: matchesMQ,
          menuClose: () => {
            setSearchOpen(false);
            handleClose(true);
          },
          searchClose: () => {
            setSearchOpen(false);
          },
        }}
        handleSnack={handleSnack}
        returnResults={resultsToAssignNum}
      />
    );
  }

  return (
    <BdrMenu
      anchorEl={anchorEl}
      ariaLabel="Contact options"
      onClose={() => {
        setPendingClient(null);
        handleClose(true);
      }}
      /*
       * Falsy entries are dropped by BdrMenu, so the owner header and its
       * divider can be written inline instead of building an array and
       * filtering it. MUI needed keys on every child because they were a raw
       * array; here `key` is optional and only given where the list is
       * conditional enough to want one.
       */
      items={
        pendingClient
          ? [
              {
                header: true,
                label: `Currently: ${owners.map(ownerLabel).join(", ")}`,
              },
              { divider: true },
              {
                label: `Add ${pendingClient?.firstName || "Client"} As Co-Owner`,
                onClick: () => fireAssign(pendingClient, false),
              },
              {
                label: `Move To ${pendingClient?.firstName || "Client"} Only`,
                onClick: () => fireAssign(pendingClient, true),
              },
              { divider: true },
              {
                /* Back to the menu's first state, not out of the menu. */
                keepOpen: true,
                label: "Cancel",
                onClick: () => setPendingClient(null),
              },
            ]
          : [
              owners.length > 0 && {
                header: true,
                label: `Owner${owners.length > 1 ? "s" : ""}: ${owners
                  .map(ownerLabel)
                  .join(", ")}`,
              },
              owners.length > 0 && { divider: true },
              {
                /* keepOpen: this swaps the menu's contents for the client
                   search on the same anchor. Closing first would clear
                   `anchorEl`, and the search would have nothing to hang off
                   -- it would look like the menu simply closed. */
                keepOpen: true,
                label:
                  owners.length > 0
                    ? "Assign To Another Client"
                    : "Assign To Client",
                onClick: () => setSearchOpen(true),
              },
              {
                label: "Create New Client",
                onClick: () => {
                  handleNewClientDisplay(true);
                  handleClose(true);
                },
              },
            ]
      }
    />
  );
};

export default MsgPageContactOptions;
